/**
 * GuidedTour — ONE shared, vertical-agnostic guided tour engine.
 *
 * Contains ZERO product or vertical wording: every string arrives as an i18n
 * key through the `steps` prop, which each vertical supplies from its own
 * config. No steps => renders nothing (never generic copy).
 *
 * Numbered bubbles, step dots, prev/next, close, dimmed background, and a
 * "seen" flag in localStorage only (no backend, no analytics).
 *
 * PHASE 1d ADDITIONS (optional, default OFF — the Command Center behaviour is
 * unchanged when they are not passed):
 *   step.anchors   array of testids/selectors; the FIRST one present wins and
 *                  is re-resolved while the tour is open, so a step can follow
 *                  a screen that changes under the finger.
 *   step.optional  a step with no anchor on screen is skipped instead of
 *                  landing on nothing.
 *   step.nextKey   per-step label for the forward button ("Let's go").
 *   tapToAdvance   tapping the REAL highlighted control moves the tour on, so
 *                  the person learns by doing instead of reading.
 *   variant        'playful' = mascot, 56px targets, larger type, soft bounce.
 *   avoid          testids the bubble must never cover.
 *   labelKeys      override the chrome labels (back/next/done/skip/close).
 *   step.noTap     this step waits for the APP, not the tap (see `goTo`).
 *   goTo           {id, n} — hand the tour to a named step from the host.
 *   cheers         three-word rewards shown where the finger landed, rotated so
 *                  the same one never lands twice in a row. They add no step and
 *                  no words to a step, and the animation dies under reduced
 *                  motion like every other play-* class.
 *   step.text      a line already resolved by the host (used when French has
 *                  to pass through the tu -> vous converter first).
 *   onStep/onFinish  side-effect hooks for the host surface.
 */
import { useState, useEffect, useLayoutEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '../ui/button';

const BUBBLE_W = 340;
const GAP = 16;
const MOBILE_W = 720;
// Dim panels: solid colour only — never backdrop-filter/blur, so the
// highlighted target stays perfectly readable.
const DIM = 'absolute bg-slate-950/55 animate-in fade-in duration-200 pointer-events-auto';
const seenKey = (id) => `yulontap.guidedTour.seen.${id}`;
const sessionKey = (id) => `yulontap.guidedTour.closed.${id}`;

const DEFAULT_LABELS = {
    back: 'commandCenter.friendly.tour.back',
    next: 'commandCenter.friendly.tour.next',
    done: 'commandCenter.friendly.tour.done',
    close: 'commandCenter.friendly.tour.close',
};

const readSeen = (id) => {
    try { return window.localStorage.getItem(seenKey(id)) === '1'; } catch { return false; }
};

// Closed once => stays closed for the rest of this browser session, even if
// the page is re-entered or remounted.
const readClosedThisSession = (id) => {
    try { return window.sessionStorage.getItem(sessionKey(id)) === '1'; } catch { return false; }
};

/** Tour state + the "seen" flag. `enabled` is false when a vertical has no config. */
export function useGuidedTour(id, enabled = true, autoStart = true) {
    const [open, setOpen] = useState(false);

    useEffect(() => {
        // `autoStart: false` = the tour exists but only ever opens on a tap.
        if (!enabled || !autoStart || !id || readSeen(id) || readClosedThisSession(id)) return;
        const timer = setTimeout(() => setOpen(true), 700);
        return () => clearTimeout(timer);
    }, [id, enabled, autoStart]);

    const start = useCallback(() => setOpen(true), []);
    const close = useCallback(() => {
        try { window.localStorage.setItem(seenKey(id), '1'); } catch { /* storage off */ }
        try { window.sessionStorage.setItem(sessionKey(id), '1'); } catch { /* storage off */ }
        setOpen(false);
    }, [id]);

    return { open, start, close };
}

/** Forget the "seen" flags so a guided tour replays (used by Reset demo). */
export function clearGuidedTourSeen() {
    try {
        Object.keys(window.localStorage)
            .filter((k) => k.startsWith('yulontap.guidedTour.seen.'))
            .forEach((k) => window.localStorage.removeItem(k));
    } catch { /* storage off */ }
}

const selectorFor = (key) => (/^[[.#]/.test(key) ? key : `[data-testid="${key}"]`);

const elementFor = (key) => {
    if (!key) return null;
    const el = document.querySelector(selectorFor(key));
    if (!el) return null;
    const r = el.getBoundingClientRect();
    if (!r.width && !r.height) return null;
    return el;
};

/** The first anchor of the step that is actually on screen right now. */
const resolveAnchor = (step) => {
    const keys = step?.anchors?.length ? step.anchors : [step?.anchor];
    for (const k of keys) {
        const el = elementFor(k);
        if (el) return el;
    }
    return null;
};

const rectOfEl = (el) => {
    if (!el) return null;
    const r = el.getBoundingClientRect();
    return { top: r.top, left: r.left, width: r.width, height: r.height };
};

/** The rect of a control the bubble must not cover — or null when that control
 *  is BEHIND something else (a full-screen sheet, say). A control nobody can
 *  see must not push the bubble around. The tour's own layers are ignored. */
const clearRectOf = (key) => {
    const el = elementFor(key);
    if (!el) return null;
    const r = el.getBoundingClientRect();
    if (!r.width || !r.height) return null;
    const cx = Math.min(window.innerWidth - 2, Math.max(2, r.left + r.width / 2));
    const cy = Math.min(window.innerHeight - 2, Math.max(2, r.top + r.height / 2));
    const tour = document.querySelector('[data-testid="guided-tour"]');
    const stack = (document.elementsFromPoint ? document.elementsFromPoint(cx, cy) : []) || [];
    const onTop = stack.find((n) => !(tour && tour.contains(n)));
    if (onTop && !(onTop === el || el.contains(onTop) || onTop.contains(el))) return null;
    return { top: r.top, left: r.left, width: r.width, height: r.height };
};

/** The lowest vertical slot that fits the bubble and clears every blocker.
 *  Blockers are the step's own target plus anything in `avoid`. With no slot
 *  big enough the bubble goes to the bottom, which is the old behaviour. */
function placeY(bubbleH, blockers, vh) {
    const PAD = 8;                       // a control's shadow is part of it
    const bands = blockers
        .filter(Boolean)
        .map((r) => ({ top: Math.max(0, r.top - PAD),
                       bottom: Math.min(vh, r.top + r.height + PAD) }))
        .filter((r) => r.bottom > r.top)
        .sort((a, b) => a.top - b.top);
    const merged = [];
    bands.forEach((r) => {
        const last = merged[merged.length - 1];
        if (last && r.top <= last.bottom + 8) last.bottom = Math.max(last.bottom, r.bottom);
        else merged.push({ ...r });
    });
    const gaps = [];
    let cursor = GAP;
    merged.forEach((r) => {
        if (r.top - cursor >= bubbleH + GAP) gaps.push([cursor, r.top]);
        cursor = Math.max(cursor, r.bottom + GAP);
    });
    if (vh - GAP - cursor >= bubbleH) gaps.push([cursor, vh - GAP]);
    if (gaps.length) {
        const [, end] = gaps[gaps.length - 1];
        return Math.max(GAP, end - bubbleH - GAP / 2);
    }
    return null;              // nothing fits — the caller decides what to give up
}

/** Where the bubble goes: a gap that clears everything; failing that a gap that
 *  at least clears the named CONTROLS (overlapping the ring is the lesser
 *  evil); failing that the bottom, which is the engine's old behaviour. */
function bubbleTop(bubbleH, ringBand, avoidBands, vh) {
    return placeY(bubbleH, [ringBand, ...avoidBands], vh)
        ?? placeY(bubbleH, avoidBands, vh)
        ?? Math.max(GAP, vh - bubbleH - GAP);
}

export function GuidedTour({
    steps, open, onClose, onDismissForever,
    tapToAdvance = false, variant = 'default', mascot = null, avoid = [],
    labelKeys = null, hideDontShow = false, onStep = null, onFinish = null,
    tapHereKey = null, goTo = null, cheers = null,
}) {
    const { t } = useTranslation();
    const [i, setI] = useState(0);
    const [rect, setRect] = useState(null);
    const [mobile, setMobile] = useState(false);
    const bubbleRef = useRef(null);
    const [cheer, setCheer] = useState(null);
    const lastCheer = useRef(-1);
    const anchorRef = useRef(null);
    const [bubbleH, setBubbleH] = useState(220);
    const playful = variant === 'playful';
    const labels = { ...DEFAULT_LABELS, ...(labelKeys || {}) };

    const total = Array.isArray(steps) ? steps.length : 0;
    const step = total ? steps[Math.min(i, total - 1)] : null;

    // Finishing, skipping or choosing "don't show again" all mean the same
    // thing: close it and never auto-open it again for this account.
    const dismiss = useCallback((reason) => {
        if (onDismissForever) onDismissForever();
        if (onFinish) onFinish(reason || 'skip');
        onClose();
    }, [onDismissForever, onFinish, onClose]);

    useEffect(() => { if (open) setI(0); }, [open]);
    useEffect(() => {
        if (open && step && onStep) onStep(step, i);
    }, [open, step, i, onStep]);

    // Bring the step's target into view, then measure it. Anchors are
    // RE-RESOLVED on a slow tick so a step can follow a screen that changes.
    useLayoutEffect(() => {
        if (!open || !step) return undefined;
        setMobile(window.innerWidth < MOBILE_W);
        let scrolled = false;
        const measure = () => {
            const el = resolveAnchor(step);
            anchorRef.current = el;
            if (el && !scrolled) {
                scrolled = true;
                const r = el.getBoundingClientRect();
                // Very tall targets (long lists): show their TOP, not their middle.
                if (r.height > window.innerHeight * 0.7) {
                    window.scrollTo({ top: window.scrollY + r.top - 100, behavior: 'smooth' });
                } else if (r.top < 0 || r.bottom > window.innerHeight) {
                    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }
            }
            setRect(rectOfEl(el));
        };
        measure();
        const timer = setTimeout(measure, 420);
        const tick = setInterval(measure, 350);
        window.addEventListener('resize', measure);
        window.addEventListener('scroll', measure, true);
        return () => {
            clearTimeout(timer);
            clearInterval(tick);
            window.removeEventListener('resize', measure);
            window.removeEventListener('scroll', measure, true);
        };
    }, [open, step]);

    // A tiny well-done, right where they tapped. Never the same one twice.
    const popCheer = useCallback(() => {
        const bank = cheers || [];
        if (!bank.length) return;
        let k = Math.floor(Math.random() * bank.length);
        if (bank.length > 1 && k === lastCheer.current) k = (k + 1) % bank.length;
        lastCheer.current = k;
        const r = anchorRef.current?.getBoundingClientRect();
        setCheer({
            text: bank[k],
            top: Math.max(6, (r ? r.top : 80) - 14),
            left: Math.min(window.innerWidth - 130, Math.max(8, (r ? r.left : 16) + 10)),
        });
        setTimeout(() => setCheer(null), 1300);
    }, [cheers]);

    useEffect(() => { if (!open) setCheer(null); }, [open]);

    const last = i === total - 1;
    const advance = useCallback(() => {
        setI((n) => (n + 1 < total ? n + 1 : n));
    }, [total]);

    // A step whose control is not on this screen is skipped, never shown empty.
    useEffect(() => {
        if (!open || !step?.optional || last) return undefined;
        const timer = setTimeout(() => { if (!anchorRef.current) advance(); }, 1300);
        return () => clearTimeout(timer);
    }, [open, step, last, advance]);

    // The host surface can hand the tour to a named step when the APP, not the
    // finger, decides the step is finished (a food actually landed on the list).
    useEffect(() => {
        if (!open || !goTo?.id) return;
        const at = (steps || []).findIndex((s) => s.id === goTo.id);
        if (at >= 0) { popCheer(); setI(at); }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [goTo?.id, goTo?.n, open]);

    // Learn by doing: a tap on the REAL control moves the tour on.
    useEffect(() => {
        if (!open || !tapToAdvance || last || step?.noTap) return undefined;
        const onDocClick = (e) => {
            const el = anchorRef.current;
            if (el && (el === e.target || el.contains(e.target))) {
                popCheer();
                setTimeout(advance, 400);
            }
        };
        document.addEventListener('click', onDocClick, true);
        return () => document.removeEventListener('click', onDocClick, true);
    }, [open, tapToAdvance, last, advance, i, step]);

    if (!open || !step) return null;

    // Position against the VISIBLE part of the target (targets can be taller
    // than the viewport). With an `avoid` list the bubble is dropped into the
    // largest free vertical gap instead: it then covers neither the step's own
    // target nor any named control (the chef hat, the points chip, a confirm).
    const vh = typeof window !== 'undefined' ? window.innerHeight : 800;
    let bubbleStyle = { left: '50%', top: '50%', transform: 'translate(-50%, -50%)' };
    let ringStyle = null;
    let cut = null;
    if (rect) {
        const visTop = Math.max(rect.top, 4);
        const visBottom = Math.min(rect.top + rect.height, vh - 4);
        ringStyle = {
            top: visTop - 6,
            left: rect.left - 6,
            width: rect.width + 12,
            height: Math.max(24, visBottom - visTop + 12),
        };
        // The un-dimmed cutout = the ring box, clamped to the viewport.
        cut = {
            top: Math.max(0, ringStyle.top),
            left: Math.max(0, ringStyle.left),
            width: Math.max(0, Math.min(ringStyle.width, window.innerWidth - Math.max(0, ringStyle.left))),
            height: Math.max(0, Math.min(ringStyle.height, vh - Math.max(0, ringStyle.top))),
        };
        const top = bubbleTop(bubbleH, { top: visTop, height: visBottom - visTop },
                              avoid.map(clearRectOf), vh);
        if (mobile) {
            bubbleStyle = { left: 16, right: 16, top, transform: 'none' };
        } else {
            const left = Math.min(
                Math.max(GAP, rect.left + rect.width / 2 - BUBBLE_W / 2),
                Math.max(GAP, window.innerWidth - BUBBLE_W - GAP),
            );
            bubbleStyle = { left, top, transform: 'none' };
        }
    } else if (mobile) {
        bubbleStyle = { left: 16, right: 16, top: Math.max(GAP, vh - bubbleH - GAP), transform: 'none' };
    }

    const tapHere = tapHereKey && !last && rect ? t(tapHereKey) : null;

    return createPortal((
        <div className="fixed inset-0 z-[120] pointer-events-none" data-testid="guided-tour" role="dialog" aria-modal="true">
            {/* Dimmed background — a CUTOUT: only the area OUTSIDE the target is
                dimmed, so the highlighted element stays fully crisp. No blur. */}
            {cut ? (
                <>
                    <div className={DIM} style={{ top: 0, left: 0, right: 0, height: cut.top }} onClick={onClose} data-testid="guided-tour-backdrop" />
                    <div className={DIM} style={{ top: cut.top, left: 0, width: cut.left, height: cut.height }} onClick={onClose} />
                    <div className={DIM} style={{ top: cut.top, left: cut.left + cut.width, right: 0, height: cut.height }} onClick={onClose} />
                    <div className={DIM} style={{ top: cut.top + cut.height, left: 0, right: 0, bottom: 0 }} onClick={onClose} />
                </>
            ) : (
                <div className={`${DIM} inset-0`} onClick={onClose} data-testid="guided-tour-backdrop" />
            )}

            {/* Soft highlight ring around the step's target */}
            {ringStyle && (
                <div
                    className={`absolute rounded-xl ring-2 ring-emerald-400/80 pointer-events-none transition-all duration-200 ${playful ? 'play-pulse' : ''}`}
                    style={ringStyle}
                    data-testid="guided-tour-ring"
                />
            )}

            {/* The reward for a correct tap: three words, 1.3 seconds, gone. */}
            {cheer && (
                <div className="absolute z-[2] rounded-full bg-amber-400 px-3 py-1 text-sm font-extrabold text-slate-900 shadow-lg pointer-events-none play-cheer"
                    style={{ top: cheer.top, left: cheer.left }}
                    data-testid="guided-tour-step-cheer">
                    {cheer.text}
                </div>
            )}

            {/* "Tap here!" — tour chrome, sits on the ring, never a step's words */}
            {tapHere && (
                <div className="absolute z-[1] rounded-full bg-emerald-600 px-2.5 py-1 text-[11px] font-bold text-white shadow-lg pointer-events-none"
                    style={{ top: Math.max(4, ringStyle.top - 26), left: Math.max(4, ringStyle.left) }}
                    data-testid="guided-tour-tap-here">
                    {tapHere}
                </div>
            )}

            <div
                ref={(el) => {
                    bubbleRef.current = el;
                    if (el && Math.abs(el.offsetHeight - bubbleH) > 8) setBubbleH(el.offsetHeight);
                }}
                className="absolute rounded-2xl border border-slate-200 bg-white p-5 shadow-2xl dark:border-slate-700 dark:bg-slate-900 animate-in fade-in duration-200 pointer-events-auto"
                style={mobile ? bubbleStyle : { ...bubbleStyle, width: BUBBLE_W }}
                data-testid="guided-tour-bubble"
            >
                <div className="flex items-start gap-3">
                    {playful && mascot ? (
                        <img src={mascot} alt="" aria-hidden="true"
                            className="mt-0.5 h-10 w-10 shrink-0" data-testid="guided-tour-mascot" />
                    ) : (
                        <span
                            className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-emerald-500 text-sm font-bold text-white"
                            data-testid="guided-tour-step-number"
                        >
                            {i + 1}
                        </span>
                    )}
                    <div className="min-w-0 flex-1">
                        <p className={`${playful ? 'text-lg' : 'text-base'} font-semibold leading-snug text-slate-900 dark:text-white break-words`}
                            data-testid="guided-tour-title">
                            {step.text || t(step.titleKey)}
                        </p>
                        {step.bodyKey ? (
                            <p className="mt-1.5 text-sm leading-relaxed text-slate-600 dark:text-slate-300 break-words"
                                data-testid="guided-tour-body">
                                {t(step.bodyKey)}
                            </p>
                        ) : null}
                    </div>
                    <button
                        onClick={onClose}
                        aria-label={t(labels.close)}
                        className="shrink-0 text-slate-400 transition-colors hover:text-slate-700 dark:hover:text-white"
                        data-testid="guided-tour-close"
                    >
                        <X className="h-4 w-4" />
                    </button>
                </div>

                <div className="mt-4 flex items-center justify-between gap-3">
                    <div className="flex items-center gap-1.5" data-testid="guided-tour-dots">
                        {steps.map((s, idx) => (
                            <button
                                key={s.titleKey}
                                onClick={() => setI(idx)}
                                aria-label={`${idx + 1}`}
                                data-testid={`guided-tour-dot-${idx}`}
                                className={`${playful ? 'h-3' : 'h-2'} rounded-full transition-all ${
                                    idx === i
                                        ? `${playful ? 'w-7' : 'w-5'} bg-emerald-500`
                                        : `${playful ? 'w-3' : 'w-2'} bg-slate-300 dark:bg-slate-600`}`}
                            />
                        ))}
                    </div>
                    <div className="flex items-center gap-1">
                        {i > 0 && (
                            <Button variant="ghost" size={playful ? 'default' : 'sm'} onClick={() => setI(i - 1)}
                                className={`px-2 text-slate-500 ${playful ? 'min-h-[56px]' : ''}`} data-testid="guided-tour-prev">
                                <ChevronLeft className="h-4 w-4" />
                                <span className="max-w-[90px] truncate">{t(labels.back)}</span>
                            </Button>
                        )}
                        <Button size={playful ? 'default' : 'sm'} onClick={() => (last ? dismiss('done') : advance())}
                            className={`bg-emerald-600 px-3 text-white hover:bg-emerald-500 ${playful ? 'min-h-[56px] text-base font-bold' : ''}`}
                            data-testid={last ? 'guided-tour-done' : 'guided-tour-next'}>
                            <span className="max-w-[140px] truncate">
                                {last ? t(labels.done) : t(step.nextKey || labels.next)}
                            </span>
                            {!last && <ChevronRight className="h-4 w-4" />}
                        </Button>
                    </div>
                </div>

                {/* Two ways out on every step, both neutral wording: skip now, or
                    never again (saved to the account, not the browser). */}
                <div className="mt-3 flex flex-wrap items-center justify-between gap-2 border-t border-slate-200 pt-2 dark:border-slate-700">
                    <button onClick={() => dismiss('skip')} data-testid="guided-tour-skip"
                        className={`px-1 font-medium text-slate-500 underline-offset-2 hover:underline dark:text-slate-400 ${
                            playful ? 'min-h-[56px] text-base font-bold' : 'min-h-[44px] text-xs'}`}>
                        {t(labels.skip || 'guidedTour.skip', 'Skip tour')}
                    </button>
                    {!hideDontShow && (
                        <button onClick={() => dismiss('skip')} data-testid="guided-tour-dont-show-again"
                            className="min-h-[44px] px-1 text-xs font-medium text-slate-500 underline-offset-2 hover:underline dark:text-slate-400">
                            {t('guidedTour.dontShowAgain', "Don't show this again")}
                        </button>
                    )}
                </div>
            </div>
        </div>
    ), document.body);
}

export default GuidedTour;
